import { ClaimFormsServiceAPI } from '@API/data-services/claim-forms-service'
import { RootState } from '@AppState/redux-store'
import { ResponseError } from '@Generated/back-office-client/src'
import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Skeleton,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { Pagination } from '@Components/common/pagination'
import { usePaginatedState } from '@Components/common/use-paginated-state'
import { formatDateToMonthDayYearString } from '@Components/util/utils'
import { Link1Icon } from '@radix-ui/react-icons'
import { Routes } from '@Components/main-app/types'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config/types'
import { ClaimFormDrawer } from './claim-form-drawer'
import { formatSearchFieldList, getSearchableFields } from './claim-form-schema-utils'
import { LinkAppointmentModal } from './link-appointment-modal'
import { formatPhoneNumber } from './phone-utils'

import './claim-form-search.scss'

const PAGE_SIZE = 20

export const ClaimFormSearch = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const linkReservationId = searchParams.get('linkReservationId')
  const initialQuery = searchParams.get('query') ?? ''

  const [query, setQuery] = useState(initialQuery)
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = usePaginatedState()
  const [showDateError, setShowDateError] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [drawerState, setDrawerState] = useState<{
    claimFormId: string
    claimantName: string
    appointmentDate?: string
  } | null>(null)
  const [linkModalState, setLinkModalState] = useState<{
    claimFormId: string
    currentReservationId: string | null
  } | null>(null)

  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'

  // Shares the drawer's query key, so opening a form and searching cost one schema fetch between them.
  const { data: schema } = useQuery({
    queryKey: ['claimFormSchema'],
    queryFn: () => ClaimFormsServiceAPI.getClaimFormSchema(),
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
  })

  // Named in the copy so an agent knows the schema-driven fields are searchable, and given a results
  // column each so a match is visible on the row. Until the schema loads the copy names only the
  // always-searchable fields rather than flickering, and no extra column renders.
  const searchableFields = useMemo(() => getSearchableFields(schema), [schema])
  const searchableFieldLabels = useMemo(
    () => searchableFields.map((field) => field.label),
    [searchableFields],
  )

  const { mutate: linkClaimForm, isPending: isLinking } = useMutation({
    mutationFn: async (claimFormId: string) => {
      setLinkError(null)
      await ClaimFormsServiceAPI.linkClaimForm(claimFormId, linkReservationId!)
    },
    onSuccess: () => {
      navigate(`${Routes.CLAIMS}?openReservation=${linkReservationId}`)
    },
    onError: (error) => {
      if (error instanceof ResponseError && error.response.status === 409) {
        setLinkError('This claim form or appointment is already linked.')
      } else {
        setLinkError('Failed to link claim form. Please try again.')
      }
    },
  })

  const hasCustomDateRange = startDate !== '' && endDate !== ''
  const hasPartialDateRange = (startDate !== '') !== (endDate !== '')

  const {
    data: searchResults,
    error,
    isLoading,
    refetch: refetchSearch,
  } = useQuery({
    queryKey: ['claim-form-search', submittedQuery, startDate, endDate, page],
    queryFn: () =>
      ClaimFormsServiceAPI.searchClaimForms(
        submittedQuery,
        page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE,
        hasCustomDateRange ? startDate : undefined,
        hasCustomDateRange ? endDate : undefined,
      ),
    enabled: !!submittedQuery && !hasPartialDateRange,
  })

  const errorMessage = useMemo(() => {
    if (!error) {
      return null
    }
    if (error instanceof ResponseError) {
      return error.message
    }
    return 'An unexpected error occurred.'
  }, [error])

  const handleSearch = () => {
    if (hasPartialDateRange) {
      setShowDateError(true)
      return
    }
    setShowDateError(false)
    if (query.trim()) {
      setSubmittedQuery(query.trim())
      setPage(0)
    }
  }

  const handleClear = () => {
    setQuery('')
    setSubmittedQuery('')
    setStartDate('')
    setEndDate('')
    setPage(0)
  }

  const handleStartDateChange = (value: string) => {
    setStartDate(value)
    setShowDateError(false)
    if (!value) {
      setEndDate('')
    }
  }

  const handleEndDateChange = (value: string) => {
    setEndDate(value)
    setShowDateError(false)
  }

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="3">
          <Heading as="h1" size="5">
            {linkReservationId ? 'Claim Form Lookup' : 'Claim Forms'}
          </Heading>
          <Text>
            Search and manage claim form submissions with or without existing claim appointments.
          </Text>

          <Flex gap="3" align="end">
            <Flex direction="column" gap="1" flexGrow="1">
              <Text size="2" weight="bold">
                Search
              </Text>
              <TextField.Root
                placeholder={`Search by ${formatSearchFieldList(['name', 'phone', 'email'], searchableFieldLabels)}`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && query.trim()) {
                    handleSearch()
                  }
                }}
              />
            </Flex>

            <Button disabled={!query.trim()} onClick={handleSearch} loading={isLoading}>
              Search
            </Button>
            <Button onClick={handleClear} color="gray" variant="soft">
              Clear
            </Button>
          </Flex>

          <Flex gap="3" align="end" justify="end">
            <Flex direction="column" gap="1">
              <Text size="2" weight="bold">
                Start Date
              </Text>
              <input
                type="date"
                className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="bold">
                End Date
              </Text>
              <input
                type="date"
                className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => handleEndDateChange(e.target.value)}
              />
            </Flex>
          </Flex>
          {showDateError ? (
            <Text color="red" size="2" align="right">
              Please select both a start and end date.
            </Text>
          ) : null}

          {errorMessage ? <Text color="red">{errorMessage}</Text> : null}

          {linkError ? <Text color="red">{linkError}</Text> : null}

          {!submittedQuery ? (
            <Text>
              Enter{' '}
              {formatSearchFieldList(['a name', 'phone number', 'email'], searchableFieldLabels)} to
              search.
            </Text>
          ) : null}

          {submittedQuery && !isLoading && searchResults?.totalResults === 0 && !errorMessage ? (
            <Text>No results found.</Text>
          ) : null}

          {submittedQuery && (isLoading || (searchResults && searchResults.totalResults > 0)) ? (
            <>
              <Skeleton loading={isLoading}>
                <Table.Root className="claim-form-search-table">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Submitted</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Player Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Phone Number</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                      {searchableFields.map((field) => (
                        <Table.ColumnHeaderCell key={field.key}>
                          {field.label}
                        </Table.ColumnHeaderCell>
                      ))}
                      <Table.ColumnHeaderCell>Appointment Date</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Form</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Claim Center</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {searchResults?.results.map((result) => (
                      <Table.Row key={result.id}>
                        <Table.RowHeaderCell>
                          {formatInTimeZone(result.createdAt, timezone, 'PP')}
                          <br />
                          {formatInTimeZone(result.createdAt, timezone, 'p')}
                        </Table.RowHeaderCell>
                        <Table.Cell>{result.name}</Table.Cell>
                        <Table.Cell>
                          {result.phone ? formatPhoneNumber(result.phone) : '\u2014'}
                        </Table.Cell>
                        <Table.Cell>{result.email ?? '\u2014'}</Table.Cell>
                        {searchableFields.map((field) => (
                          <Table.Cell key={field.key}>
                            {/* Every ticket's value for the field, whichever one the query matched. */}
                            {result.searchableFieldValues?.[field.key]?.join(', ') ?? '\u2014'}
                          </Table.Cell>
                        ))}
                        <Table.Cell>
                          {result.claimReservation ? (
                            formatDateToMonthDayYearString(
                              parseISO(result.claimReservation.timeslot.display.localDate),
                              timezone,
                            )
                          ) : linkReservationId ? (
                            <Button
                              variant="ghost"
                              color="red"
                              size="1"
                              disabled={isLinking}
                              onClick={() => linkClaimForm(result.id)}>
                              <Link1Icon />
                              Link Claim Form
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              color="red"
                              size="1"
                              onClick={() =>
                                setLinkModalState({
                                  claimFormId: result.id,
                                  currentReservationId: result.claimReservationId ?? null,
                                })
                              }>
                              <Link1Icon />
                              Link Existing Appointment
                            </Button>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <button
                            type="button"
                            className="claim-form-open-form"
                            onClick={() =>
                              setDrawerState({
                                claimFormId: result.id,
                                claimantName: result.name,
                                appointmentDate: result.claimReservation
                                  ? formatDateToMonthDayYearString(
                                      parseISO(result.claimReservation.timeslot.display.localDate),
                                      timezone,
                                    )
                                  : undefined,
                              })
                            }>
                            Open Form
                          </button>
                        </Table.Cell>
                        <Table.Cell>
                          {result.claimReservation?.claimCenter?.name ?? '\u2014'}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Skeleton>
              {searchResults?.totalResults ? (
                <Pagination
                  totalItems={searchResults.totalResults}
                  pageSize={PAGE_SIZE}
                  currentPage={page}
                  onPageChange={setPage}
                />
              ) : null}
            </>
          ) : null}
        </Flex>

        {drawerState ? (
          <ClaimFormDrawer
            key={drawerState.claimFormId}
            claimFormId={drawerState.claimFormId}
            claimantName={drawerState.claimantName}
            appointmentDate={drawerState.appointmentDate}
            onClose={() => setDrawerState(null)}
            onAuthorized={() => refetchSearch()}
            onLinkAppointment={(formId) => {
              if (linkReservationId) {
                linkClaimForm(formId)
              } else {
                const result = searchResults?.results.find((r) => r.id === formId)
                setLinkModalState({
                  claimFormId: formId,
                  currentReservationId: result?.claimReservationId ?? null,
                })
              }
            }}
          />
        ) : null}

        {linkModalState ? (
          <LinkAppointmentModal
            claimFormId={linkModalState.claimFormId}
            currentReservationId={linkModalState.currentReservationId}
            onSuccess={() => setLinkModalState(null)}
            onClose={() => setLinkModalState(null)}
          />
        ) : null}
      </Section>
    </Container>
  )
}
